import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_a3709e67-7648-4bcb-a123-219697b944ef/artifacts/aqwsfdgh_Martin_Aagaard_-_Kystlandskap_-_IMG_9313.jpg';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');
  
  // Check if user came through proper invite flow
  const validatedCode = sessionStorage.getItem('validated_invite_code');

  useEffect(() => {
    // If no invite code in URL or session, redirect to invite page
    if (!inviteCode && !validatedCode) {
      toast.error('Please enter an invite code first');
      navigate('/invite');
      return;
    }
    
    if (user) {
      navigate('/portal');
    }
  }, [user, navigate, inviteCode, validatedCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, name);
      
      // Mark invite code as used
      const codeToUse = inviteCode || validatedCode;
      if (codeToUse) {
        try {
          await api.post(`/invite-codes/${codeToUse}/use`);
        } catch (e) {
          console.log('Could not mark invite code as used');
        }
      }
      
      // Clear session storage
      sessionStorage.removeItem('validated_invite_code');
      
      toast.success('Account created successfully');
      
      // Show Beehive payment link (placeholder)
      // In production, this will redirect to the actual Beehive payment page
      const BEEHIVE_PAYMENT_URL = 'https://beehiiv.com/payment'; // Placeholder URL
      toast.info('Complete your subscription setup', {
        description: 'Click to proceed to payment',
        action: {
          label: 'Continue',
          onClick: () => window.open(BEEHIVE_PAYMENT_URL, '_blank')
        },
        duration: 10000
      });
      
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no valid invite code
  if (!inviteCode && !validatedCode) {
    return null;
  }

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div
        className="absolute inset-0 bg-center bg-cover opacity-30"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-[var(--cream)]/80" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/" className="text-lg text-[var(--navy-800)] font-light tracking-widest">
            COASTWISE TRADE
          </Link>
        </header>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-semibold text-[var(--navy-800)] text-center mb-2">
              Create Account
            </h1>
            <p className="text-center text-sm text-[var(--muted-foreground)] mb-6">
              Invite code: <span className="font-mono font-semibold">{inviteCode || validatedCode}</span>
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--navy-800)] mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                className="btn-big btn-big-primary flex items-center justify-center"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create Account'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
              Already have an account?{' '}
              <Link to="/login" className="text-[var(--navy-800)] font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
